import { useEffect, useRef, useState } from 'react';
import type HlsType from 'hls.js';

interface VideoPlayerProps {
	/** Link HLS; ưu tiên dùng nếu có. */
	src: string | null;
	/** Link iframe của player phimapi — dùng khi không có/không phát được m3u8. */
	embedUrl: string | null;
	poster?: string;
	title: string;
	autoPlay?: boolean;
	/** Gọi định kỳ (mỗi ~5s) với tiến độ 0–100 để lưu vào lịch sử xem. */
	onProgress?: (percent: number) => void;
}

const PROGRESS_INTERVAL_MS = 5000;

export function VideoPlayer({
	src,
	embedUrl,
	poster,
	title,
	autoPlay = false,
	onProgress,
}: VideoPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const lastProgressAt = useRef(0);
	const [fatalError, setFatalError] = useState<string | null>(null);
	/** Chuyển sang iframe khi HLS chết hẳn (CORS, link hết hạn...). */
	const [useEmbed, setUseEmbed] = useState(false);

	useEffect(() => {
		setFatalError(null);
		setUseEmbed(!src && Boolean(embedUrl));
	}, [src, embedUrl]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !src || useEmbed) return;

		let hls: HlsType | null = null;
		let cancelled = false;

		// hls.js nặng ~600 KB nên chỉ nạp khi thật sự phát phim, không nằm
		// trong bundle khởi động.
		void import('hls.js').then(({ default: Hls }) => {
			if (cancelled) return;

			if (Hls.isSupported()) {
				hls = new Hls({ enableWorker: true, lowLatencyMode: false });
				hls.loadSource(src);
				hls.attachMedia(video);
				hls.on(Hls.Events.ERROR, (_event, data) => {
					if (!data.fatal) return;
					// Còn embed thì âm thầm đổi sang iframe, không bắt người dùng chịu lỗi.
					if (embedUrl) {
						setUseEmbed(true);
					} else {
						setFatalError(
							'Không phát được nguồn phim này. Thử server khác nhé.',
						);
					}
				});
			} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
				// Safari / iOS phát HLS thẳng.
				video.src = src;
			} else if (embedUrl) {
				setUseEmbed(true);
			} else {
				setFatalError('Trình duyệt của bạn không hỗ trợ phát HLS.');
			}
		});

		return () => {
			cancelled = true;
			hls?.destroy();
			video.pause();
			video.removeAttribute('src');
			video.load();
		};
	}, [src, embedUrl, useEmbed]);

	// autoPlay riêng: gọi play() sau khi nguồn đã gắn, và bỏ qua lỗi
	// NotAllowedError do chính sách chặn autoplay của trình duyệt.
	useEffect(() => {
		const video = videoRef.current;
		if (!video || !autoPlay || useEmbed || !src) return;
		void video.play().catch(() => undefined);
	}, [autoPlay, src, useEmbed]);

	if (useEmbed && embedUrl) {
		return (
			<div className="overflow-hidden rounded-card bg-[#05060c]">
				<iframe
					src={embedUrl}
					title={title}
					allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
					allowFullScreen
					referrerPolicy="no-referrer"
					className="block aspect-video w-full border-0"
				/>
			</div>
		);
	}

	return (
		<div className="overflow-hidden rounded-card bg-[#05060c]">
			<video
				ref={videoRef}
				controls
				playsInline
				poster={poster}
				title={title}
				className="block aspect-video w-full bg-[#05060c]"
				onTimeUpdate={(event) => {
					if (!onProgress) return;
					const now = Date.now();
					if (now - lastProgressAt.current < PROGRESS_INTERVAL_MS) return;
					lastProgressAt.current = now;

					const { currentTime, duration } = event.currentTarget;
					if (!Number.isFinite(duration) || duration <= 0) return;
					onProgress(Math.min(100, (currentTime / duration) * 100));
				}}
			/>
			{fatalError && (
				<p className="border-t border-line px-4 py-3 text-sm text-accent-soft">
					{fatalError}
				</p>
			)}
		</div>
	);
}
